import React, { useState, useMemo, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet";

import { materialPropertyService } from "/app/src/services";

import { Search } from "/app/src/components/generic/components/search";

import { useTranslation } from "react-i18next";
import { MaterialProperty as MaterialPropertyType } from "/app/src/models";
import { createColumnHelper } from "@tanstack/react-table";
import Table from "/app/src/components/generic/tables/table";
import { useSortUpgrade } from "/app/src/hooks/useSortUpgrade";
import usePaginatedData from "/app/src/hooks/usePaginatedData";
import useQueryParams from "/app/src/hooks/useQueryParams";
/**
 * Renders the material properties explorer component which displays a table of material properties.
 *
 * @returns {JSX.Element} The rendered material properties component.
 */
export default function MaterialProperties() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [sort, setSort] = useSortUpgrade([]);
  const [searchString, setSearchString] = useState(
    searchParams.get("search") || "",
  );
  const [page, setPage] = useState(
    parseInt(searchParams.get("page") || "1") - 1,
  );
  const [pageSize, setPageSize] = useState(
    parseInt(searchParams.get("pageSize") || "25"),
  );

  const columnHelper = createColumnHelper<MaterialPropertyType>();
  const columns = useMemo(
    () => [
      columnHelper.accessor("name", {
        id: "name",
        cell: (info) => info.getValue(),
        header: t("translation:name"),
      }),
      columnHelper.accessor("expiryDatePickType", {
        id: "expiryDatePickType",
        cell: (info) => info.getValue(),
        header: t("translation:expiry"),
      }),
    ],
    [columnHelper, t],
  );

  const goToMaterialProperty = useCallback(
    (materialPropertyId: number) => {
      navigate(`/explorer/material_properties/${materialPropertyId}`);
    },
    [navigate],
  );

  const {
    data: materialProperties,
    count: materialPropertiesCount,
    isFetching,
  } = usePaginatedData<MaterialPropertyType>({
    queryKey: ["material_properties"],
    searchString,
    sort,
    page,
    pageSize,
    service: materialPropertyService,
  });

  const { updatePage, updatePageSize } = useQueryParams({
    count: materialPropertiesCount,
    isFetching,
    page,
    setPage,
    pageSize,
    setPageSize,
  });

  return (
    <div className="explorer">
      <Helmet>
        <title>{t("translation:material_properties")} - ItemPath</title>
      </Helmet>
      <Search allowClear setSearchString={setSearchString} />
      <Table
        loading={isFetching}
        rows={materialProperties}
        tableColumns={columns}
        length={materialPropertiesCount}
        sort={sort}
        setSort={setSort}
        paginationEnabled={{
          currentPage: page,
          pageSize,
          setPage: updatePage,
          setPageSize: updatePageSize,
        }}
        enableRowSelection
        rowClicked={goToMaterialProperty}
        emptyText={`${t("translation:no")} ${t(
          "translation:material_properties",
        )} ${t("translation:found")}`}
      />
    </div>
  );
}
